import { theme } from 'antd';
import type { ThemeConfig } from 'antd';
import type { AppSettings } from './app-settings';

const fontFamily =
  '"HarmonyOS Sans","Segoe UI","SF Pro Display",-apple-system,BlinkMacSystemFont,Roboto,Oxygen,Ubuntu,Cantarell,"Open Sans","Helvetica Neue",sans-serif,"HarmonyOS Sans SC","PingFang SC","Hiragino Sans GB","Microsoft Yahei UI","Microsoft Yahei","Source Han Sans CN",sans-serif,"Segoe UI Emoji","Segoe UI Symbol","Apple Color Emoji","Twemoji Mozilla","Noto Color Emoji","Android Emoji"';
const fontFamilyCode =
  'Hack,ui-monospace,SFMono-Regular,SF Mono,Menlo,Consolas,"HarmonyOS Sans SC","PingFang SC","Hiragino Sans GB","Microsoft Yahei UI","Microsoft Yahei","Source Han Sans CN",sans-serif,"Segoe UI Emoji","Segoe UI Symbol","Apple Color Emoji","Twemoji Mozilla","Noto Color Emoji","Android Emoji"';

export const defaultPrimaryColor = '#2F4CA6';

const lobePrimary = {
  dark: [
    '#000000',
    '#111111',
    '#333333',
    '#555555',
    '#666666',
    '#888888',
    '#aaaaaa',
    '#cccccc',
    '#dddddd',
    '#eeeeee',
    '#ffffff',
    '#ffffff',
    '#ffffff',
  ],
  light: [
    '#ffffff',
    '#f5f5f5',
    '#eeeeee',
    '#cccccc',
    '#aaaaaa',
    '#888888',
    '#666666',
    '#444444',
    '#333333',
    '#222222',
    '#111111',
    '#0b0b0b',
    '#000000',
  ],
} as const;

const lobeColors = {
  gray: {
    light: [
      '#ffffff',
      '#f8f8f8',
      '#eeeeee',
      '#e3e3e3',
      '#dddddd',
      '#cccccc',
      '#bbbbbb',
      '#aaaaaa',
      '#999999',
      '#888888',
      '#666666',
      '#333333',
      '#080808',
    ],
    lightA: [
      'rgba(0, 0, 0, 0.015)',
      'rgba(0, 0, 0, 0.03)',
      'rgba(0, 0, 0, 0.06)',
      'rgba(0, 0, 0, 0.12)',
      'rgba(0, 0, 0, 0.18)',
      'rgba(0, 0, 0, 0.24)',
      'rgba(0, 0, 0, 0.32)',
      'rgba(0, 0, 0, 0.38)',
      'rgba(0, 0, 0, 0.44)',
      'rgba(0, 0, 0, 0.5)',
      'rgba(0, 0, 0, 0.68)',
      'rgba(0, 0, 0, 0.84)',
      'rgba(0, 0, 0, 0.98)',
    ],
  },
  green: {
    light: [
      '#ffffff',
      '#f4fdeb',
      '#e7f8dd',
      '#d8f2ce',
      '#c7eabd',
      '#b4e1ac',
      '#a0d79b',
      '#89cc8a',
      '#71c179',
      '#379d4a',
      '#007944',
      '#003f28',
      '#000503',
    ],
  },
  gold: {
    light: [
      '#ffffff',
      '#fffdff',
      '#fff9f7',
      '#fff4eb',
      '#ffeedc',
      '#ffe6c8',
      '#ffdcaf',
      '#ffd190',
      '#ffc368',
      '#ee9e0b',
      '#d59300',
      '#6a4900',
      '#070300',
    ],
  },
  volcano: {
    light: [
      '#ffffff',
      '#fff7f6',
      '#ffece9',
      '#ffded9',
      '#ffcec5',
      '#ffbbaf',
      '#ffa695',
      '#ff8e78',
      '#fb745a',
      '#ec5e41',
      '#a53716',
      '#5d1900',
      '#0c0100',
    ],
  },
  geekblue: {
    light: [
      '#ffffff',
      '#fcfbff',
      '#f1f5ff',
      '#e2ebff',
      '#cedfff',
      '#b6d0ff',
      '#9abeff',
      '#78a9ff',
      '#4c90ff',
      '#0072f5',
      '#005ae0',
      '#002a8a',
      '#00021b',
    ],
  },
} as const;

export const createLobeTheme = ({
  themeMode,
  primaryColor,
  compact,
}: Pick<
  AppSettings,
  'themeMode' | 'primaryColor' | 'compact'
>): ThemeConfig => {
  const isDark = themeMode === 'dark';
  const isNeutral = primaryColor.toLowerCase() === '#222222';
  const algorithm = [
    isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    ...(compact ? [theme.compactAlgorithm] : []),
  ];
  const derived = theme.getDesignToken({
    algorithm,
    token: {
      colorPrimary: primaryColor,
      colorSuccess: lobeColors.green.light[9],
      colorWarning: lobeColors.gold.light[9],
      colorError: lobeColors.volcano.light[9],
      colorInfo: lobeColors.geekblue.light[9],
    },
  });
  const lobeGray = isDark
    ? ([
        '#141414',
        '#1f1f1f',
        '#292929',
        '#333333',
        '#424242',
        '#555555',
        '#666666',
        '#777777',
        '#999999',
        '#aaaaaa',
        '#bbbbbb',
        '#dddddd',
        '#f8f8f8',
      ] as const)
    : lobeColors.gray.light;
  const lobeGrayA = isDark
    ? lobeColors.gray.lightA.map((color) =>
        color.replace('0, 0, 0', '255, 255, 255'),
      )
    : lobeColors.gray.lightA;
  const neutralBorder = lobeGray[3];
  const lobeGreen = lobeColors.green.light;
  const lobeGold = lobeColors.gold.light;
  const lobeVolcano = lobeColors.volcano.light;
  const lobeGeekblue = lobeColors.geekblue.light;
  const primaryScale = isDark ? lobePrimary.dark : lobePrimary.light;
  // Dark surfaces need lighter text without lightening solid button fills.
  const primaryTextPalette = theme.getDesignToken({
    token: { colorPrimary: primaryColor },
  });
  const neutralPrimaryTokens = {
    colorPrimaryBg: primaryScale[1],
    colorPrimaryBgHover: primaryScale[2],
    colorPrimaryBorder: primaryScale[4],
    colorPrimaryBorderHover: primaryScale[3],
    colorPrimaryHover: primaryScale[8],
    colorPrimary: primaryScale[9],
    colorPrimaryActive: primaryScale[10],
    colorPrimaryTextHover: primaryScale[8],
    colorPrimaryText: primaryScale[9],
    colorPrimaryTextActive: primaryScale[10],
  } as const;

  const primaryTokens: ThemeConfig['token'] = {
    ...(isNeutral
      ? neutralPrimaryTokens
      : Object.fromEntries(
          Object.entries(derived).filter(([key]) =>
            key.startsWith('colorPrimary'),
          ),
        )),
    ...(isDark && !isNeutral
      ? {
          colorPrimaryText: primaryTextPalette.colorPrimaryBorder,
          colorPrimaryTextHover: primaryTextPalette.colorPrimaryBgHover,
          colorPrimaryTextActive: primaryTextPalette.colorPrimaryBorder,
        }
      : {}),
    ...(!isDark &&
    primaryColor.toLowerCase() === defaultPrimaryColor.toLowerCase()
      ? {
          colorPrimaryBg: '#F0F4FD',
          colorPrimaryBgHover: '#E5ECFB',
          colorPrimaryBorder: '#CFD9EF',
        }
      : {}),
  };
  const statusTokens = isDark
    ? Object.fromEntries(
        Object.entries(derived).filter(([key]) =>
          /^color(Success|Warning|Error|Info)/.test(key),
        ),
      )
    : {};

  return {
    algorithm,
    cssVar: { key: 'app' },
    token: {
      ...primaryTokens,
      colorSuccess: lobeGreen[9],
      colorWarning: lobeGold[9],
      colorError: lobeVolcano[9],
      colorInfo: lobeGeekblue[9],
      colorTextBase: lobeGray[12],
      colorBgBase: isDark ? '#000000' : lobeGray[0],
      colorTextLightSolid: '#f8f8f8',
      colorSuccessBg: lobeGreen[1],
      colorSuccessBgHover: lobeGreen[2],
      colorSuccessBorder: lobeGreen[4],
      colorSuccessBorderHover: lobeGreen[3],
      colorSuccessHover: lobeGreen[8],
      colorSuccessActive: lobeGreen[10],
      colorSuccessText: lobeGreen[9],
      colorSuccessTextHover: lobeGreen[8],
      colorSuccessTextActive: lobeGreen[10],
      colorWarningBg: lobeGold[1],
      colorWarningBgHover: lobeGold[2],
      colorWarningBorder: lobeGold[4],
      colorWarningBorderHover: lobeGold[3],
      colorWarningHover: lobeGold[8],
      colorWarningActive: lobeGold[10],
      colorWarningText: lobeGold[9],
      colorWarningTextHover: lobeGold[8],
      colorWarningTextActive: lobeGold[10],
      colorErrorBg: lobeVolcano[1],
      colorErrorBgHover: lobeVolcano[2],
      colorErrorBorder: lobeVolcano[4],
      colorErrorBorderHover: lobeVolcano[3],
      colorErrorHover: lobeVolcano[8],
      colorErrorActive: lobeVolcano[10],
      colorErrorText: lobeVolcano[9],
      colorErrorTextHover: lobeVolcano[8],
      colorErrorTextActive: lobeVolcano[10],
      colorInfoBg: lobeGeekblue[1],
      colorInfoBgHover: lobeGeekblue[2],
      colorInfoBorder: lobeGeekblue[4],
      colorInfoBorderHover: lobeGeekblue[3],
      colorInfoHover: lobeGeekblue[8],
      colorInfoActive: lobeGeekblue[10],
      colorInfoText: lobeGeekblue[9],
      colorInfoTextHover: lobeGeekblue[8],
      colorInfoTextActive: lobeGeekblue[10],
      ...statusTokens,
      ...(isDark
        ? {
            colorSuccessText: lobeGreen[8],
            colorSuccessTextHover: lobeGreen[7],
            colorSuccessTextActive: lobeGreen[8],
            colorInfoText: lobeGeekblue[7],
            colorInfoTextHover: lobeGeekblue[6],
            colorInfoTextActive: lobeGeekblue[7],
          }
        : {}),
      colorLink: primaryTokens.colorPrimaryText,
      colorLinkHover: primaryTokens.colorPrimaryTextHover,
      colorLinkActive: primaryTokens.colorPrimaryTextActive,
      colorText: lobeGray[12],
      colorTextSecondary: lobeGray[10],
      colorTextTertiary: lobeGray[8],
      colorTextQuaternary: lobeGray[6],
      colorTextDisabled: lobeGray[6],
      colorBgContainer: lobeGray[0],
      colorBgElevated: lobeGray[0],
      colorBgLayout: isDark ? '#111111' : lobeGray[1],
      colorBgSpotlight: isDark ? '#424242' : lobeGray[4],
      colorBgMask: isDark ? 'rgba(0, 0, 0, 0.6)' : lobeGrayA[8],
      colorBorder: neutralBorder,
      colorBorderSecondary: lobeGray[2],
      colorSplit: lobeGray[2],
      colorFill: lobeGrayA[3],
      colorFillSecondary: lobeGrayA[2],
      colorFillTertiary: lobeGrayA[1],
      colorFillQuaternary: lobeGrayA[0],
      controlOutline: lobeGrayA[3],
      fontSize: 14,
      fontSizeSM: 12,
      fontFamily,
      fontFamilyCode,
      lineHeight: 1.5714,
      borderRadius: 8,
      borderRadiusXS: 4,
      borderRadiusSM: 6,
      borderRadiusLG: 12,
      controlHeight: compact ? 32 : 36,
      controlHeightSM: compact ? 24 : 28,
      controlHeightLG: compact ? 36 : 40,
      boxShadow:
        '0 40px 80px rgba(0, 0, 0, 0.06), 0 20px 40px rgba(0, 0, 0, 0.05), 0 10px 20px rgba(0, 0, 0, 0.04), 0 5px 10px rgba(0, 0, 0, 0.03), 0 2px 4px rgba(0, 0, 0, 0.02)',
      boxShadowSecondary:
        '0 17.5px 23.4px rgba(0, 0, 0, 0.04), 0 9.4px 12.5px rgba(0, 0, 0, 0.03), 0 5.25px 7px rgba(0, 0, 0, 0.02), 0 2.8px 3.7px -2px rgba(0, 0, 0, 0.01), 0 1.2px 1.5px rgba(0, 0, 0, 0.01)',
      boxShadowTertiary:
        '0 1px 2px rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.02)',
    },
    components: {
      Tooltip: { colorTextLightSolid: lobeGray[12] },
      Button: {
        colorTextLightSolid: '#f8f8f8',
        primaryColor: isDark && isNeutral ? '#111111' : '#f8f8f8',
        primaryShadow: 'none',
        defaultShadow: 'none',
        dangerShadow: 'none',
        contentFontSizeSM: 12,
        defaultBorderColor: neutralBorder,
        defaultColor: lobeGray[12],
        defaultBg: lobeGray[0],
        defaultHoverBg: lobeGrayA[1],
        defaultHoverBorderColor: neutralBorder,
        defaultHoverColor: lobeGray[12],
        defaultActiveBg: lobeGrayA[2],
        defaultActiveBorderColor: neutralBorder,
        borderRadius: 8,
        fontWeight: 500,
      },
      Input: {
        activeShadow: 'none',
        hoverBorderColor: primaryTokens.colorPrimaryHover,
        activeBorderColor: primaryTokens.colorPrimary,
        borderRadius: 6,
      },
      InputNumber: {
        activeShadow: 'none',
        hoverBorderColor: primaryTokens.colorPrimaryHover,
        activeBorderColor: primaryTokens.colorPrimary,
      },
      Mentions: {
        hoverBorderColor: primaryTokens.colorPrimaryHover,
        activeBorderColor: primaryTokens.colorPrimary,
      },
      Select: {
        activeBorderColor: primaryTokens.colorPrimary,
        hoverBorderColor: primaryTokens.colorPrimaryHover,
        optionSelectedBg: primaryTokens.colorPrimaryBg,
        optionActiveBg: lobeGrayA[1],
        optionSelectedFontWeight: 500,
        borderRadius: 6,
      },
      DatePicker: {
        activeBorderColor: primaryTokens.colorPrimary,
        hoverBorderColor: primaryTokens.colorPrimaryHover,
      },
      Alert: {
        borderRadiusLG: 8,
      },
      Modal: {
        borderRadiusLG: 12,
      },
      Card: {
        borderRadiusLG: 12,
      },
      Progress: {
        circleTextColor: lobeGray[12],
        defaultColor: primaryTokens.colorPrimary,
        remainingColor: lobeGrayA[2],
      },
      Switch: {
        trackHeight: 22,
        trackMinWidth: 44,
      },
      Checkbox: {
        colorWhite: isDark && isNeutral ? '#111111' : '#ffffff',
        borderRadiusSM: 4,
      },
      Layout: {
        bodyBg: isDark ? '#111111' : lobeGray[1],
        footerBg: isDark ? '#111111' : lobeGray[1],
        headerBg: lobeGray[0],
        headerColor: lobeGray[12],
        siderBg: lobeGray[0],
        triggerBg: lobeGray[1],
        triggerColor: lobeGray[12],
      },
      Menu: {
        activeBarBorderWidth: 0,
        itemBg: 'transparent',
        subMenuItemBg: 'transparent',
        itemColor: lobeGray[10],
        itemHoverColor: lobeGray[12],
        itemSelectedColor: primaryTokens.colorPrimaryText,
        horizontalItemSelectedColor: primaryTokens.colorPrimaryText,
        horizontalItemHoverColor: primaryTokens.colorPrimaryTextHover,
        itemHoverBg: lobeGrayA[1],
        itemSelectedBg: primaryTokens.colorPrimaryBg,
      },
      Tabs: {
        itemSelectedColor: primaryTokens.colorPrimaryText,
        itemHoverColor: primaryTokens.colorPrimaryTextHover,
        itemActiveColor: primaryTokens.colorPrimaryTextActive,
      },
      Pagination: {
        colorPrimary: primaryTokens.colorPrimaryText,
        colorPrimaryHover: primaryTokens.colorPrimaryTextHover,
      },
      Notification: {
        colorSuccessBg: isDark ? derived.colorSuccessBg : lobeGreen[1],
        colorErrorBg: isDark ? derived.colorErrorBg : lobeVolcano[1],
        colorInfoBg: isDark ? derived.colorInfoBg : lobeGeekblue[1],
        colorWarningBg: isDark ? derived.colorWarningBg : lobeGold[2],
      },
    },
  };
};
